import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Menu,
  MenuButton,
  Text,
  useMediaQuery,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { BsGrid, BsListUl } from 'react-icons/bs';
import { FiChevronLeft, FiChevronRight, FiDatabase, FiFileText, FiSearch } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ActionMenuList } from 'components/ActionMenuList';
import FigmaCheckCircleIcon from 'components/Icons/FigmaCheckCircleIcon';
import FigmaEditIcon from 'components/Icons/FigmaEditIcon';
import FigmaPlusIcon from 'components/Icons/FigmaPlusIcon';
import FigmaSearchIcon from 'components/Icons/FigmaSearchIcon';
import FigmaTrashIcon from 'components/Icons/FigmaTrashIcon';
import FigmaTriangleDown from 'components/Icons/FigmaTriangleDown';
import DeleteModal from 'components/Modals/DeleteModal';
import {
  PLAN_ACCENT_BLUE,
  PLAN_CHART_SERIES_COLORS,
  PLAN_HEADER_HORIZONTAL_PADDING,
  PLAN_REVAMP_RADIUS_SM,
  PLAN_CARD_PADDING,
  PLAN_CHART_CARD_RADIUS,
  PLAN_TABLE_CELL_BORDER,
  PLAN_TABLE_CELL_PX,
  PLAN_TABLE_HEADER_BG,
  PLAN_TEXT,
  controlHeight,
} from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import { PLAN_DRIVER_MID_GREY } from 'pages/PlanRevamp/PlanPageUIRevamp/planInputPanelTheme';
import {
  deleteDataImportFile,
  duplicateSourceFile,
  editDataImportFile,
  getDataImportFiles,
  selectedFileId,
} from 'redux/DataImportSlice';
import { handleFileDelete } from 'services/s3Service';
import { fontSize, shadow } from 'styles/revampTokens';
import { AppDispatch } from 'utils/GlobalHelpers';

import {
  ALL_TYPES,
  formatCreatedOn,
  formatLastUpdated,
  formatRowCount,
  getTypeLabel,
  isRecent,
  pluralise,
  type SortKey,
  type SourceFile,
} from './sourcesTableUtils';
import EditSourceModal from './EditSourceModal';
import useSourceStats from './useSourceStats';
import colors from './dataTheme';
import { StateBlock } from './detailUi';

const PAGE_SIZE = 10;
const COLUMNS_WIDE = 'minmax(0, 3fr) repeat(6, minmax(0, 1fr)) 112px';
// Below ~1200px Versions and Rows move out of their own columns — the line
// under the source name already carries both, so nothing is lost.
const COLUMNS_COMPACT = 'minmax(0, 3fr) repeat(2, minmax(0, 1fr)) 112px';

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'recent', label: 'Recently updated' },
  { key: 'oldest', label: 'Oldest updated' },
  { key: 'nameAsc', label: 'Name A–Z' },
  { key: 'nameDesc', label: 'Name Z–A' },
];

const BASE_TYPES = ['CSV'];

interface Properties {
  sources: SourceFile[];
  onAddSource: () => void;
  onOpenSource: () => void;
}

const SourcesTable = ({ sources, onAddSource, onOpenSource }: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const { modelId } = useParams();
  const stats = useSourceStats(sources);
  const [isWide] = useMediaQuery('(min-width: 1200px)');
  const columns = isWide ? COLUMNS_WIDE : COLUMNS_COMPACT;

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>(ALL_TYPES);
  const [sortKey, setSortKey] = useState<SortKey>('recent');
  const [view, setView] = useState<'list' | 'grid'>('list');
  const [page, setPage] = useState(1);
  const [editTarget, setEditTarget] = useState<SourceFile | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SourceFile | null>(null);

  const typeOptions = useMemo(
    () => [
      ALL_TYPES,
      ...Array.from(new Set([...BASE_TYPES, ...sources.map((source) => getTypeLabel(source))])),
    ],
    [sources],
  );

  const visibleSources = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = sources.filter(
      (source) =>
        (typeFilter === ALL_TYPES || getTypeLabel(source) === typeFilter) &&
        (!term || `${source.file_name}`.toLowerCase().includes(term)),
    );
    const updatedOf = (source: SourceFile) =>
      stats[source.id]?.lastUpdated || source.date_created || '';
    return [...filtered].sort((a, b) => {
      if (sortKey === 'nameAsc') return `${a.file_name}`.localeCompare(`${b.file_name}`);
      if (sortKey === 'nameDesc') return `${b.file_name}`.localeCompare(`${a.file_name}`);
      if (sortKey === 'oldest') return updatedOf(a).localeCompare(updatedOf(b));
      return updatedOf(b).localeCompare(updatedOf(a));
    });
  }, [sources, stats, search, typeFilter, sortKey]);

  const pageCount = Math.max(1, Math.ceil(visibleSources.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageSources = visibleSources.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const openSource = (source: SourceFile) => {
    onOpenSource();
    dispatch(selectedFileId({ id: source.id, name: source.file_name, deatsilsPage: true }));
  };

  const refetch = () => dispatch(getDataImportFiles(modelId));

  const saveName = async (name: string) => {
    if (!editTarget) return;
    await dispatch(
      editDataImportFile({
        modelId,
        fileId: editTarget.id,
        request_body: { file_name: name, file_type: 'csv' },
        toaster: {
          successMessage: 'Successfully updated',
          errorMessage: 'An Source with this name already exists',
        },
      }),
    );
    setEditTarget(null);
    refetch();
  };

  const duplicateSource = async (source: SourceFile) => {
    await dispatch(duplicateSourceFile(source.id));
    refetch();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await handleFileDelete(deleteTarget.file_name, 'blox-data-import');
    await dispatch(deleteDataImportFile({ modelId, fileId: deleteTarget.id }));
    setDeleteTarget(null);
    refetch();
  };

  const renderDropdown = (
    label: string,
    options: { key: string; label: string }[],
    selected: string,
    onSelect: (key: string) => void,
    minW: string,
  ) => (
    <Menu matchWidth>
      <MenuButton
        as={Button}
        variant='unstyled'
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          height: controlHeight,
          padding: 0,
          width: minW,
          minWidth: minW,
          fontSize: fontSize.body,
          fontWeight: 400,
          color: colors.textDark,
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: PLAN_REVAMP_RADIUS_SM,
          cursor: 'pointer',
        }}
        _hover={{ bg: colors.hover }}
        _active={{ bg: colors.hover }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '6px',
            width: '100%',
            padding: '0 10px',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
          <FigmaTriangleDown width={8} height={4} color={colors.textSecondary} />
        </span>
      </MenuButton>
      <ActionMenuList
        minW={minW}
        borderRadius={PLAN_REVAMP_RADIUS_SM}
        items={options.map((option) => ({
          key: option.key,
          label: option.label,
          icon:
            option.key === selected ? (
              <FigmaCheckCircleIcon width={14} height={14} color={colors.primary} />
            ) : (
              <Box
                w='14px'
                h='14px'
                borderRadius='full'
                border='1.5px solid'
                borderColor={PLAN_DRIVER_MID_GREY}
              />
            ),
          onSelect: () => {
            onSelect(option.key);
            setPage(1);
          },
        }))}
      />
    </Menu>
  );

  const renderRowActions = (source: SourceFile) => (
    <Flex gap='2px' alignItems='center' onClick={(event) => event.stopPropagation()}>
      <IconButton
        aria-label='Rename'
        icon={<FigmaEditIcon width={13} height={13} color={colors.textSecondary} />}
        h='28px'
        w='28px'
        minW='28px'
        variant='ghost'
        borderRadius='6px'
        _hover={{ bg: colors.hover, color: colors.textDark }}
        onClick={() => setEditTarget(source)}
      />
      <IconButton
        aria-label='Duplicate'
        icon={
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='13'
            height='13'
            viewBox='0 0 16 16'
            fill='none'
          >
            <path
              d='M13 0H8.33333C6.67933 0 5.33333 1.346 5.33333 3V7.66667C5.33333 9.32067 6.67933 10.6667 8.33333 10.6667H13C14.654 10.6667 16 9.32067 16 7.66667V3C16 1.346 14.654 0 13 0ZM14 7.66667C14 8.218 13.5513 8.66667 13 8.66667H8.33333C7.782 8.66667 7.33333 8.218 7.33333 7.66667V3C7.33333 2.44867 7.782 2 8.33333 2H13C13.5513 2 14 2.44867 14 3V7.66667ZM10.6667 13C10.6667 14.654 9.32067 16 7.66667 16H3C1.346 16 0 14.654 0 13V8.33333C0 6.67933 1.346 5.33333 3 5.33333C3.552 5.33333 4 5.78067 4 6.33333C4 6.886 3.552 7.33333 3 7.33333C2.448 7.33333 2 7.782 2 8.33333V13C2 13.5513 2.44867 14 3 14H7.66667C8.218 14 8.66667 13.5513 8.66667 13C8.66667 12.4487 9.11467 12 9.66667 12C10.2187 12 10.6667 12.4473 10.6667 13Z'
              fill='currentColor'
            />
          </svg>
        }
        h='28px'
        w='28px'
        minW='28px'
        variant='ghost'
        borderRadius='6px'
        color={colors.textSecondary}
        _hover={{ bg: colors.hover, color: colors.textDark }}
        onClick={() => duplicateSource(source)}
      />
      <IconButton
        aria-label='Delete'
        icon={<FigmaTrashIcon width={13} height={13} color={colors.danger} />}
        h='28px'
        w='28px'
        minW='28px'
        variant='ghost'
        borderRadius='6px'
        _hover={{ bg: `${colors.danger}15` }}
        onClick={() => setDeleteTarget(source)}
      />
    </Flex>
  );

  const renderIcon = (index: number) => {
    const tint = PLAN_CHART_SERIES_COLORS[index % PLAN_CHART_SERIES_COLORS.length];
    return (
      <Flex
        w='32px'
        h='32px'
        flexShrink={0}
        alignItems='center'
        justifyContent='center'
        borderRadius={PLAN_REVAMP_RADIUS_SM}
        bg={`${tint}1F`}
        color={tint}
      >
        <FiFileText size={15} />
      </Flex>
    );
  };

  const renderBadge = (source: SourceFile) => {
    const label = getTypeLabel(source);
    const isBlue = label === 'XLSX';
    return (
      <Text
        as='span'
        display='inline-block'
        px={2}
        py='2px'
        borderRadius='6px'
        {...PLAN_TEXT.badge}
        color={isBlue ? PLAN_ACCENT_BLUE : colors.primary}
        bg={isBlue ? `${PLAN_ACCENT_BLUE}1F` : colors.activeBg}
      >
        {label}
      </Text>
    );
  };

  const renderName = (source: SourceFile) => (
    <Text {...PLAN_TEXT.toolbarTab} color={colors.textDark} noOfLines={1} title={source.file_name}>
      {source.file_name}
    </Text>
  );

  const metaLine = (source: SourceFile) => {
    const stat = stats[source.id];
    if (!stat) return ' ';
    return `${pluralise(stat.versions, 'version')} · ${formatRowCount(stat.rows)} rows`;
  };

  const renderRow = (source: SourceFile, index: number) => {
    const stat = stats[source.id];
    const updated = stat?.lastUpdated || source.date_created;
    return (
      <Box
        key={source.id}
        display='grid'
        gridTemplateColumns={columns}
        alignItems='center'
        columnGap={6}
        px={PLAN_TABLE_CELL_PX}
        py='10px'
        cursor='pointer'
        borderTop='1px solid'
        borderColor={PLAN_TABLE_CELL_BORDER}
        transition='background 0.12s ease'
        _hover={{ bg: colors.hover }}
        onClick={() => openSource(source)}
      >
        <Flex alignItems='center' gap='10px' minW={0}>
          {renderIcon(index)}
          <Box minW={0}>
            {renderName(source)}
            <Text {...PLAN_TEXT.labelSm} color={colors.textSecondary} noOfLines={1}>
              {metaLine(source)}
            </Text>
          </Box>
        </Flex>
        <Box>{renderBadge(source)}</Box>
        {isWide && (
          <>
            <Text {...PLAN_TEXT.tableComfortaaCell} color={colors.textDark}>
              {stat ? stat.versions : '—'}
            </Text>
            <Text {...PLAN_TEXT.tableComfortaaCell} color={colors.textDark}>
              {formatRowCount(stat?.rows)}
            </Text>
          </>
        )}
        {isWide && (
          <>
            <Text
              {...PLAN_TEXT.tableComfortaaCell}
              color={colors.textSecondary}
              noOfLines={1}
              title={source.created_by}
            >
              {source.created_by || '—'}
            </Text>
            <Text {...PLAN_TEXT.tableComfortaaCell} color={colors.textSecondary}>
              {formatCreatedOn(source.date_created)}
            </Text>
          </>
        )}
        <Flex alignItems='center' gap={2}>
          <Box
            w='8px'
            h='8px'
            borderRadius='full'
            flexShrink={0}
            bg={isRecent(updated) ? colors.success : PLAN_DRIVER_MID_GREY}
          />
          <Text {...PLAN_TEXT.tableComfortaaCell} color={colors.textSecondary}>
            {formatLastUpdated(updated)}
          </Text>
        </Flex>
        <Flex justifyContent='flex-end'>{renderRowActions(source)}</Flex>
      </Box>
    );
  };

  const renderCard = (source: SourceFile, index: number) => {
    const stat = stats[source.id];
    const tint = PLAN_CHART_SERIES_COLORS[index % PLAN_CHART_SERIES_COLORS.length];
    const updated = stat?.lastUpdated || source.date_created;

    const actionButton = (
      label: string,
      icon: JSX.Element,
      onClick: () => void,
      hoverBg: string,
    ) => (
      <IconButton
        aria-label={label}
        icon={icon}
        h='24px'
        w='24px'
        minW='24px'
        variant='ghost'
        borderRadius='6px'
        _hover={{ bg: hoverBg }}
        onClick={onClick}
      />
    );

    const statBlock = (label: string, value: string, withDivider?: boolean) => (
      <Box
        flex={1}
        px={3}
        py='10px'
        borderLeft={withDivider ? '1px solid' : undefined}
        borderColor={PLAN_TABLE_CELL_BORDER}
      >
        <Text
          {...PLAN_TEXT.badge}
          color={colors.textSecondary}
          textTransform='uppercase'
          letterSpacing='0.04em'
        >
          {label}
        </Text>
        <Text {...PLAN_TEXT.tableComfortaaCell} fontWeight={600} color={colors.textDark} mt='2px'>
          {value}
        </Text>
      </Box>
    );

    return (
      <Box
        key={source.id}
        role='group'
        cursor='pointer'
        border='1px solid'
        borderColor={colors.border}
        borderRadius={PLAN_CHART_CARD_RADIUS}
        bg={colors.surface}
        overflow='hidden'
        transition='border-color 0.2s ease'
        _hover={{ borderColor: colors.primary }}
        onClick={() => openSource(source)}
      >
        <Box
          position='relative'
          h='76px'
          bgImage={`radial-gradient(${tint}44 1px, transparent 1px), linear-gradient(135deg, ${tint}2E 0%, ${tint}0D 100%)`}
          bgSize='12px 12px, 100% 100%'
        >
          <Box
            position='absolute'
            top='10px'
            left='12px'
            bg={colors.surface}
            borderRadius='6px'
            boxShadow={shadow.panel}
          >
            {renderBadge(source)}
          </Box>

          <Flex
            position='absolute'
            top='8px'
            right='8px'
            p='2px'
            gap='1px'
            bg={colors.surface}
            borderRadius={PLAN_REVAMP_RADIUS_SM}
            boxShadow={shadow.overlay}
            opacity={0}
            transform='translateY(-2px)'
            transition='opacity 0.15s ease, transform 0.15s ease'
            _groupHover={{ opacity: 1, transform: 'translateY(0)' }}
            onClick={(event) => event.stopPropagation()}
          >
            {actionButton(
              'Rename',
              <FigmaEditIcon width={13} height={13} color={colors.textSecondary} />,
              () => setEditTarget(source),
              colors.hover,
            )}
            {actionButton(
              'Duplicate',
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='13'
                height='13'
                viewBox='0 0 16 16'
                fill='none'
                style={{ color: colors.textSecondary }}
              >
                <path
                  d='M13 0H8.33333C6.67933 0 5.33333 1.346 5.33333 3V7.66667C5.33333 9.32067 6.67933 10.6667 8.33333 10.6667H13C14.654 10.6667 16 9.32067 16 7.66667V3C16 1.346 14.654 0 13 0ZM14 7.66667C14 8.218 13.5513 8.66667 13 8.66667H8.33333C7.782 8.66667 7.33333 8.218 7.33333 7.66667V3C7.33333 2.44867 7.782 2 8.33333 2H13C13.5513 2 14 2.44867 14 3V7.66667ZM10.6667 13C10.6667 14.654 9.32067 16 7.66667 16H3C1.346 16 0 14.654 0 13V8.33333C0 6.67933 1.346 5.33333 3 5.33333C3.552 5.33333 4 5.78067 4 6.33333C4 6.886 3.552 7.33333 3 7.33333C2.448 7.33333 2 7.782 2 8.33333V13C2 13.5513 2.44867 14 3 14H7.66667C8.218 14 8.66667 13.5513 8.66667 13C8.66667 12.4487 9.11467 12 9.66667 12C10.2187 12 10.6667 12.4473 10.6667 13Z'
                  fill='currentColor'
                />
              </svg>,
              () => duplicateSource(source),
              colors.hover,
            )}
            {actionButton(
              'Delete',
              <FigmaTrashIcon width={13} height={13} color={colors.danger} />,
              () => setDeleteTarget(source),
              `${colors.danger}15`,
            )}
          </Flex>

          <Flex
            position='absolute'
            left='12px'
            bottom='-18px'
            w='40px'
            h='40px'
            alignItems='center'
            justifyContent='center'
            bg={colors.surface}
            color={tint}
            border='1px solid'
            borderColor={colors.border}
            borderRadius={PLAN_REVAMP_RADIUS_SM}
            boxShadow={shadow.panel}
          >
            <FiFileText size={18} />
          </Flex>
        </Box>

        <Box px={3} pt='26px' pb={3}>
          <Text
            {...PLAN_TEXT.toolbarTab}
            fontWeight={600}
            color={colors.textDark}
            noOfLines={1}
            title={source.file_name}
          >
            {source.file_name}
          </Text>
          <Flex alignItems='center' gap='6px' mt='3px'>
            <Box
              w='6px'
              h='6px'
              borderRadius='full'
              flexShrink={0}
              bg={isRecent(updated) ? colors.success : PLAN_DRIVER_MID_GREY}
            />
            <Text {...PLAN_TEXT.labelSm} color={colors.textSecondary} noOfLines={1}>
              Updated {formatLastUpdated(updated)}
            </Text>
          </Flex>
        </Box>

        <Flex borderTop='1px solid' borderColor={PLAN_TABLE_CELL_BORDER} bg={colors.headerBg}>
          {statBlock('Versions', stat ? `${stat.versions}` : '—')}
          {statBlock('Rows', formatRowCount(stat?.rows), true)}
        </Flex>
      </Box>
    );
  };

  const toggleButton = (mode: 'list' | 'grid', icon: JSX.Element, label: string) => (
    <IconButton
      aria-label={label}
      icon={icon}
      size='sm'
      h='26px'
      w='28px'
      minW='28px'
      borderRadius='6px'
      variant='ghost'
      bg={view === mode ? colors.activeBg : 'transparent'}
      color={view === mode ? colors.primary : colors.textSecondary}
      _hover={{ bg: colors.hover }}
      onClick={() => setView(mode)}
    />
  );

  const footerText =
    pageCount > 1
      ? `Showing ${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(
          currentPage * PAGE_SIZE,
          visibleSources.length,
        )} of ${pluralise(visibleSources.length, 'source')}`
      : `Showing ${pluralise(visibleSources.length, 'source')}`;

  return (
    <Box px={PLAN_HEADER_HORIZONTAL_PADDING} pb={3} w='100%'>
      {/* Same shell as the Plan page's table card: white surface, one hairline,
          card padding, and the table itself in its own bordered box. */}
      <Box
        bg={colors.surface}
        border='1px solid'
        borderColor={colors.border}
        borderRadius={PLAN_CHART_CARD_RADIUS}
        overflow='hidden'
      >
        <Box
          style={{ padding: PLAN_CARD_PADDING, display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          {/* Search bar */}
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              width: '260px',
              flexShrink: 0,
              height: controlHeight,
              padding: '0 10px',
              border: `1px solid ${colors.border}`,
              borderRadius: PLAN_REVAMP_RADIUS_SM,
              background: colors.surface,
            }}
          >
            <FigmaSearchIcon width={13} height={13} color={colors.textSecondary} />
            <Input
              variant='unstyled'
              placeholder='Search sources...'
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              style={{
                fontSize: fontSize.body,
                fontWeight: 400,
                color: colors.textDark,
                padding: 0,
              }}
              _placeholder={{ color: colors.textSecondary }}
            />
          </Box>

          {/* Spacer */}
          <Box style={{ flex: 1 }} />

          {/* Right side controls */}
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            {renderDropdown(
              typeFilter === ALL_TYPES ? 'All types' : typeFilter,
              typeOptions.map((type) => ({
                key: type,
                label: type === ALL_TYPES ? 'All types' : type,
              })),
              typeFilter,
              setTypeFilter,
              '120px',
            )}
            {renderDropdown(
              SORT_OPTIONS.find((option) => option.key === sortKey)?.label ?? 'Recently updated',
              SORT_OPTIONS,
              sortKey,
              (key) => setSortKey(key as SortKey),
              '160px',
            )}
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                height: controlHeight,
                padding: '0 3px',
                gap: '2px',
                border: `1px solid ${colors.border}`,
                borderRadius: PLAN_REVAMP_RADIUS_SM,
                background: colors.surface,
              }}
            >
              {toggleButton('list', <BsListUl size={14} />, 'List view')}
              {toggleButton('grid', <BsGrid size={14} />, 'Grid view')}
            </Box>
          </Box>
        </Box>

        <Box px={PLAN_CARD_PADDING} pb={PLAN_CARD_PADDING}>
          {view === 'list' ? (
            <Box
              border='1px solid'
              borderColor={PLAN_TABLE_CELL_BORDER}
              borderRadius={PLAN_REVAMP_RADIUS_SM}
              overflow='hidden'
            >
              <Box
                display='grid'
                gridTemplateColumns={columns}
                columnGap={6}
                px={PLAN_TABLE_CELL_PX}
                py='8px'
                bg={PLAN_TABLE_HEADER_BG}
              >
                <Text {...PLAN_TEXT.tableComfortaa} color={colors.textDark}>
                  Source
                </Text>
                <Text {...PLAN_TEXT.tableComfortaa} color={colors.textDark}>
                  Type
                </Text>
                {isWide && (
                  <>
                    <Text {...PLAN_TEXT.tableComfortaa} color={colors.textDark}>
                      Versions
                    </Text>
                    <Text {...PLAN_TEXT.tableComfortaa} color={colors.textDark}>
                      Rows
                    </Text>
                  </>
                )}
                {isWide && (
                  <>
                    <Text {...PLAN_TEXT.tableComfortaa} color={colors.textDark}>
                      Created by
                    </Text>
                    <Text {...PLAN_TEXT.tableComfortaa} color={colors.textDark}>
                      Created on
                    </Text>
                  </>
                )}
                <Text {...PLAN_TEXT.tableComfortaa} color={colors.textDark}>
                  Last updated
                </Text>
                <Text {...PLAN_TEXT.tableComfortaa} color={colors.textDark} textAlign='right'>
                  Actions
                </Text>
              </Box>
              {pageSources.map((source, index) => renderRow(source, index))}
              {visibleSources.length === 0 && (
                <Box borderTop='1px solid' borderColor={PLAN_TABLE_CELL_BORDER}>
                  <StateBlock
                    icon={sources.length === 0 ? <FiDatabase size={22} /> : <FiSearch size={22} />}
                    title={
                      sources.length === 0 ? 'No source data yet' : 'No sources match your filters'
                    }
                    description={
                      sources.length === 0
                        ? 'Add your first source to start working with your model data.'
                        : 'Try a different search or clear a filter.'
                    }
                    action={
                      sources.length === 0 ? (
                        <Button
                          h={controlHeight}
                          px={4}
                          {...PLAN_TEXT.toolbarTab}
                          width='fit-content'
                          minW='unset'
                          bg={colors.action}
                          color={colors.white}
                          borderRadius={PLAN_REVAMP_RADIUS_SM}
                          _hover={{ bg: colors.actionHover }}
                          leftIcon={<FigmaPlusIcon width={13} height={13} color={colors.white} />}
                          onClick={onAddSource}
                        >
                          Add source
                        </Button>
                      ) : undefined
                    }
                  />
                </Box>
              )}
            </Box>
          ) : (
            <>
              <Box
                display='grid'
                gridTemplateColumns='repeat(auto-fill, minmax(240px, 1fr))'
                gap={3}
              >
                {pageSources.map((source, index) => renderCard(source, index))}
              </Box>
              {visibleSources.length === 0 && (
                <Box borderColor={PLAN_TABLE_CELL_BORDER}>
                  <StateBlock
                    icon={sources.length === 0 ? <FiDatabase size={22} /> : <FiSearch size={22} />}
                    title={
                      sources.length === 0 ? 'No source data yet' : 'No sources match your filters'
                    }
                    description={
                      sources.length === 0
                        ? 'Add your first source to start working with your model data.'
                        : 'Try a different search or clear a filter.'
                    }
                    action={
                      sources.length === 0 ? (
                        <Button
                          h={controlHeight}
                          px={4}
                          {...PLAN_TEXT.toolbarTab}
                          width='fit-content'
                          minW='unset'
                          bg={colors.action}
                          color={colors.white}
                          borderRadius={PLAN_REVAMP_RADIUS_SM}
                          _hover={{ bg: colors.actionHover }}
                          leftIcon={<FigmaPlusIcon width={13} height={13} color={colors.white} />}
                          onClick={onAddSource}
                        >
                          Add source
                        </Button>
                      ) : undefined
                    }
                  />
                </Box>
              )}
            </>
          )}
        </Box>

        <Flex
          px={PLAN_CARD_PADDING}
          pb={PLAN_CARD_PADDING}
          alignItems='center'
          justifyContent='space-between'
        >
          <Text {...PLAN_TEXT.labelSm} color={colors.textSecondary}>
            {footerText}
          </Text>
          {pageCount > 1 && (
            <Flex alignItems='center' gap={1}>
              <IconButton
                aria-label='Previous page'
                icon={<FiChevronLeft size={16} />}
                size='sm'
                variant='ghost'
                color={colors.textSecondary}
                isDisabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
              />
              {Array.from({ length: pageCount }, (_, index) => index + 1).map((pageNumber) => (
                <Button
                  key={pageNumber}
                  size='sm'
                  minW='32px'
                  h='32px'
                  borderRadius={PLAN_REVAMP_RADIUS_SM}
                  {...PLAN_TEXT.labelSmMedium}
                  variant='ghost'
                  bg={pageNumber === currentPage ? colors.activeBg : 'transparent'}
                  color={pageNumber === currentPage ? colors.primary : colors.textSecondary}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </Button>
              ))}
              <IconButton
                aria-label='Next page'
                icon={<FiChevronRight size={16} />}
                size='sm'
                variant='ghost'
                color={colors.textSecondary}
                isDisabled={currentPage === pageCount}
                onClick={() => setPage(currentPage + 1)}
              />
            </Flex>
          )}
        </Flex>
      </Box>

      <EditSourceModal
        isOpen={Boolean(editTarget)}
        initialName={editTarget?.file_name ?? ''}
        onClose={() => setEditTarget(null)}
        onSave={saveName}
      />

      <DeleteModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        itemName={deleteTarget?.file_name ?? ''}
        onDelete={confirmDelete}
      />
    </Box>
  );
};

export default SourcesTable;
